import { Fragment, useContext, useState, useEffect } from "react";
import { Disclosure, Menu, Transition } from "@headlessui/react";
import { Bars3Icon, BellIcon, XMarkIcon, UserCircleIcon, ChevronDownIcon } from "@heroicons/react/24/outline";
import AuthContext from "../AuthContext";
import SideMenu from "./SideMenu";
import Sidebar from "./Sidebar";
import { Link, useLocation } from "react-router-dom";

const navigation = [
  { name: "Dashboard", href: "/", current: true },
  { name: "Inventory", href: "/inventory", current: false },
  { name: "Purchase Details", href: "/purchase-details", current: false },
  { name: "Sales", href: "/sales", current: false },
  { name: "Returned Products", href: "/returned-products", current: false },
];

const userNavigation = [{ name: "Sign out", href: "./login" }];

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export default function Header({ onOpenSubscriptionModal = () => {}, subscriptionPlan = null, planLoaded = false }) {
  const authContext = useContext(AuthContext);
  const localStorageData = JSON.parse(localStorage.getItem("user") || "{}") || {};
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const isSuperRoute = location.pathname.startsWith("/superadmin");
  const segments = (location.pathname || "").replace(/^\/+|\/+$/g, "").split("/");
  const nonTenantPrefixes = ["api", "login", "register", "forgot-password", "verify-email", "reset-password", "static", "assets", "favicon.ico", "superadmin"];
  const storedTenant = localStorage.getItem("tenant");
  let base = "";
  if (storedTenant) base = `/${storedTenant}`;
  else if (segments.length >= 2 && segments[0].toLowerCase() === "t") base = `/t/${segments[1]}`;
  else if (segments.length >= 1 && segments[0] && !nonTenantPrefixes.includes(segments[0].toLowerCase())) base = `/${segments[0]}`;

  const signOut = () => {
    try { authContext.signout(); } catch (e) { /* ignore */ }
    try { localStorage.removeItem("user"); } catch {}
    try { localStorage.removeItem("tenant"); } catch {}
    try { localStorage.removeItem("isSuperAdmin"); } catch {}
    window.location.href = "/login";
  };

  // Close drawer when route changes to ensure it always closes after navigation
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  return (
    <>
      <div className="min-h-full">
        <Disclosure as="nav" className="bg-gray-800">
          {() => (
            <>
              <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <div className="flex h-16 items-center justify-between">
                  {/* Left logo */}
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="flex justify-center items-center gap-2">
                        <img
                          className="h-8"
                          src={require("../assets/stokraze.png")}
                          alt="Inventory Management System"
                        />
                        <span className="font-bold text-white hidden sm:inline">
                          | Inventory Management System
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Desktop account dropdown and tenant name */}
                  <div className="hidden md:flex items-center gap-4">
                    <div className="text-sm text-gray-200">
                      {localStorageData?.tenantName || localStorage.getItem("tenantName") || localStorage.getItem("tenant") || "Store"}
                    </div>
                    <Menu as="div" className="relative">
                      <Menu.Button className="flex items-center gap-2 rounded-md border border-gray-500 bg-transparent px-3 py-1.5 text-sm font-medium text-gray-200 hover:bg-gray-700 hover:border-gray-400">
                        <UserCircleIcon className="h-5 w-5" />
                        <span>Account</span>
                        <ChevronDownIcon className="h-4 w-4" />
                      </Menu.Button>

                      <Transition
                        as={Fragment}
                        enter="transition ease-out duration-100"
                        enterFrom="transform opacity-0 scale-95"
                        enterTo="transform opacity-100 scale-100"
                        leave="transition ease-in duration-75"
                        leaveFrom="transform opacity-100 scale-100"
                        leaveTo="transform opacity-0 scale-95"
                      >
                        <Menu.Items className="absolute right-0 z-50 mt-2 w-48 origin-top-right rounded-xl border border-[#C7D2FE]/15 bg-[#111B31] py-2 shadow-xl shadow-[#020617]/40 focus:outline-none">
                          {!isSuperRoute && (
                            <Menu.Item>
                              {({ active }) => (
                                <Link
                                  to={`${base}/profile`}
                                  className={classNames(
                                    active ? "bg-[#8A17D1]/20 text-white" : "text-[#C7D2FE]",
                                    "block px-4 py-2 text-sm"
                                  )}
                                >
                                  Profile
                                </Link>
                              )}
                            </Menu.Item>
                          )}
                          <Menu.Item>
                            {({ active }) => (
                              <button
                                type="button"
                                onClick={signOut}
                                className={classNames(
                                  active ? "bg-[#D326AD]/20 text-white" : "text-[#C7D2FE]",
                                  "block w-full px-4 py-2 text-left text-sm"
                                )}
                              >
                                Sign Out
                              </button>
                            )}
                          </Menu.Item>
                        </Menu.Items>
                      </Transition>
                    </Menu>
                  </div>

                  {/* Mobile menu button */}
                  <div className="-mr-2 flex md:hidden">
                    <button onClick={() => setDrawerOpen(true)} aria-label="Open main menu" className="inline-flex items-center justify-center rounded-md bg-gray-800 p-2 text-gray-400 hover:bg-gray-700 hover:text-white focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-gray-800">
                      <span className="sr-only">Open main menu</span>
                      <Bars3Icon className="block h-6 w-6" aria-hidden="true" />
                    </button>
                  </div>
                </div>
              </div>
              {/* Mobile menu drawer controlled by state */}
              {drawerOpen && (
                <div className="md:hidden fixed inset-0 z-40 flex">
                  <div className="fixed inset-0 bg-black bg-opacity-30" aria-hidden="true" onClick={() => setDrawerOpen(false)}></div>
                  <div className="relative ml-0 w-64 sm:w-72 max-w-full bg-white shadow-xl h-full transform transition-transform duration-200 ease-in-out translate-x-0" style={{ willChange: 'transform' }}>
                    <div className="flex items-center justify-between px-4 py-3 border-b">
                      <div className="flex items-center gap-2">
                        <img className="h-7" src={require("../assets/stokraze.png")} alt="logo" />
                        <span className="font-semibold">Menu</span>
                      </div>
                      <button onClick={() => setDrawerOpen(false)} className="inline-flex items-center justify-center rounded-md p-2 text-gray-600 hover:bg-gray-100">
                        <span className="sr-only">Close menu</span>
                        <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                      </button>
                    </div>

                    <div className="overflow-y-auto h-full p-2">
                      {isSuperRoute ? (
                        <Sidebar isMobile={true} onNavigate={() => setDrawerOpen(false)} />
                      ) : (
                        <SideMenu className="block bg-white p-0" isMobile={true} subscriptionPlan={subscriptionPlan} planLoaded={planLoaded} onOpenSubscriptionModal={() => { onOpenSubscriptionModal(); setDrawerOpen(false); }} onNavigate={() => setDrawerOpen(false)} />
                      )}

                      <div className="mt-4 border-t pt-4">
                        <div className="px-4">
                          {!isSuperRoute && (
                            <Link
                              to={`${base}/profile`}
                              onClick={() => setDrawerOpen(false)}
                              className="mb-3 flex w-full items-center justify-center gap-2 rounded-md border border-[#8A17D1]/30 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100"
                            >
                              <UserCircleIcon className="h-5 w-5" />
                              Profile
                            </Link>
                          )}

                          <button
                            type="button"
                            onClick={signOut}
                            className="w-full rounded-md bg-pink-600 hover:bg-pink-700 text-white px-3 py-2 text-sm font-medium"
                          >
                            Sign Out
                          </button>

                          <div className="mt-3 text-center text-sm text-gray-500">
                            {localStorageData?.tenantName || localStorage.getItem("tenantName") || localStorage.getItem("tenant") || "Store"}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </Disclosure>
      </div>
    </>
  );
}
